'use strict';

import { CoreRepository } from '../CoreRepository';
import { Report } from '../../Entities/Reports/Report';
import { ReportCollection } from '../../Collections/Reports/ReportCollection';
import { ReportFilterOption } from '../../Entities/Reports/Agency/ReportFilterOption';
import { ReportFilterOptionCollection } from '../../Collections/Reports/Agency/ReportFilterOptionCollection';

export class AgencyReportRepository extends CoreRepository {
  /**
   * Fetches a report from the API, hydrates each row into a Report entity,
   * and returns a fully populated ReportCollection.
   *
   * @param userId - The ID of the user making the request
   * @param agencyId - The ID of the agency associated with the report
   * @param reportName - The key/name of the report to retrieve
   * @param options - Optional query parameters (e.g., pagination, sorting)
   * @param criteria - Optional filtering criteria to be included in options
   * @returns Promise<ReportCollection> - A collection of hydrated Report entities
   */
  public getReport(
    userId: number,
    agencyId: number,
    reportName: string,
    options: object = {},
    criteria: object = null
  ): Promise<ReportCollection> {
    if (!options) {
      options = {};
    }

    if (criteria) {
      options['criteria'] = criteria;
    }

    return this.webAPI.getReport(userId, agencyId, reportName, options).then((response) => {
      const collection = new ReportCollection();

      response.data.forEach((rowData: Record<string, any>) => {
        const row = new Report().hydrate(rowData);
        collection.addItem(row);
      });

      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  /**
   * Exports a report as a CSV file and triggers a browser download.
   *
   * @param userId - ID of the user requesting the export
   * @param agencyId - ID of the agency associated with the report
   * @param reportName - The report's name or identifier (used for filename)
   * @param options - Additional API options (e.g., pagination, sorting)
   * @param criteria - Optional filters to apply to the export request
   * @returns Promise<void> - Resolves when the file download is triggered
   */
  public getReportExport(
    userId: number,
    agencyId: number,
    reportName: string,
    options: object = {},
    criteria: object = null
  ): Promise<void> {
    if (criteria) {
      options = { ...options, criteria };
    }

    return this.webAPI
      .getReportExport(userId, agencyId, reportName, options)
      .then((csvText: string) => {
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `${reportName}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);
      });
  }

  /**
   * Retrieves filter options for a specific report and returns them
   * as a hydrated ReportFilterOptionCollection.
   *
   * @param userId - The ID of the user requesting the filter options
   * @param agencyId - The ID of the agency context for the request
   * @param reportName - The name or key of the report
   * @returns Promise<ReportFilterOptionCollection> - A collection of filter options
   */
  public getReportFilterOptions(
    userId: number,
    agencyId: number,
    reportName: string
  ): Promise<ReportFilterOptionCollection> {
    return this.webAPI.getReportFilterOptions(userId, agencyId, reportName).then((response) => {
      const collection = new ReportFilterOptionCollection();

      response.forEach((rowData: ReportFilterOption) => {
        const row = new ReportFilterOption().hydrate(rowData);
        collection.addItem(row);
      });

      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }
}
